/* bb-stock-delivery.js
   <bb-stock-delivery> custom element :
   - Tick chaque seconde, MàJ countdown jusqu'à l'heure limite d'expédition
   - Calcule la date de livraison (locale fr-FR) en respectant :
     · jours d'expédition = lundi à samedi (dimanche fermé)
     · cutoff configurable via data-cutoff-hour (défaut 16)
     · délai = data-delivery-days jours ouvrés ajoutés à la date d'expédition
   - Affiche/masque le bandeau countdown selon état stock + jour/heure
*/
(function () {
  if (window.customElements && customElements.get('bb-stock-delivery')) return;

  function pad(n) { return String(n).padStart(2, '0'); }

  // "Maintenant" dans le fuseau de la boutique (CET), pas celui du visiteur.
  // new Date(localeString) recrée une Date dont les champs locaux (getHours/getDay/
  // getDate) reflètent l'heure murale boutique. now et target reçoivent le même
  // décalage → (target - now) reste la vraie durée restante.
  function storeNow() {
    try {
      return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
    } catch (e) {
      return new Date();
    }
  }

  // Jour d'expédition : lundi (1) à samedi (6). Dimanche (0) fermé.
  function isShipDay(day) { return day >= 1 && day <= 6; }

  // Avance la date au prochain jour d'expédition (skip dimanches)
  function nextShipDay(d) {
    do {
      d.setDate(d.getDate() + 1);
    } while (!isShipDay(d.getDay()));
    return d;
  }

  // Date d'expédition réelle : aujourd'hui si avant cutoff & jour ouvré, sinon prochain ouvré
  function computeShipDate(now, cutoffHour) {
    var d = new Date(now);
    var beforeCutoff = isShipDay(d.getDay()) && d.getHours() < cutoffHour;
    if (!beforeCutoff) {
      d = nextShipDay(d);
    }
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // Date de livraison : ship + delivery_days jours d'expédition successifs
  function computeDeliveryDate(shipDate, deliveryDays) {
    var d = new Date(shipDate);
    var added = 0;
    while (added < deliveryDays) {
      d = nextShipDay(d);
      added++;
    }
    return d;
  }

  function isSameDate(a, b) {
    return a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
  }

  function isTomorrow(d, now) {
    var tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return isSameDate(d, tomorrow);
  }

  function formatWeekday(d, capitalize, locale) {
    var s = d.toLocaleDateString(locale || 'fr-FR', { weekday: 'long' });
    return capitalize ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  }

  function formatRelativeWeekday(d, now, capitalize, locale, tomorrowLabel) {
    if (isTomorrow(d, now)) {
      var tomorrow = tomorrowLabel || 'demain';
      return capitalize ? tomorrow.charAt(0).toUpperCase() + tomorrow.slice(1) : tomorrow;
    }
    return formatWeekday(d, capitalize, locale);
  }

  function formatDeliveryChoice(expressDate, classicDate, now, locale, tomorrowLabel) {
    var expressLabel = formatRelativeWeekday(expressDate, now, true, locale, tomorrowLabel);
    if (isSameDate(expressDate, classicDate)) return expressLabel;
    return expressLabel + ' / ' + formatRelativeWeekday(classicDate, now, false, locale, tomorrowLabel);
  }

  function localizedFallbacks(locale) {
    var lang = (locale || 'fr').toLowerCase().split('-')[0];
    var labels = {
      fr: { tomorrow: 'demain', dispatchedToday: 'Expédié aujourd’hui', dispatchedPrefix: 'Expédié', countdownPrefix: 'plus que', readyToShip: 'Prêt à expédier' },
      en: { tomorrow: 'tomorrow', dispatchedToday: 'Dispatched today', dispatchedPrefix: 'Dispatched', countdownPrefix: 'only', readyToShip: 'Ready to ship' },
      de: { tomorrow: 'morgen', dispatchedToday: 'Heute versandt', dispatchedPrefix: 'Versand', countdownPrefix: 'nur noch', readyToShip: 'Versandbereit' },
      es: { tomorrow: 'mañana', dispatchedToday: 'Enviado hoy', dispatchedPrefix: 'Enviado', countdownPrefix: 'solo quedan', readyToShip: 'Listo para enviar' },
      it: { tomorrow: 'domani', dispatchedToday: 'Spedito oggi', dispatchedPrefix: 'Spedito', countdownPrefix: 'mancano solo', readyToShip: 'Pronto per la spedizione' },
      nl: { tomorrow: 'morgen', dispatchedToday: 'Vandaag verzonden', dispatchedPrefix: 'Verzonden', countdownPrefix: 'nog maar', readyToShip: 'Klaar voor verzending' },
      pt: { tomorrow: 'amanhã', dispatchedToday: 'Enviado hoje', dispatchedPrefix: 'Enviado', countdownPrefix: 'faltam apenas', readyToShip: 'Pronto para envio' },
      ja: { tomorrow: '明日', dispatchedToday: '本日発送', dispatchedPrefix: '発送', countdownPrefix: '残り', readyToShip: '発送準備完了' }
    };
    return labels[lang] || labels.en;
  }

  class BbStockDelivery extends HTMLElement {
    connectedCallback() {
      this._cutoff = parseInt(this.dataset.cutoffHour, 10) || 16;
      this._days = parseInt(this.dataset.deliveryDays, 10) || 3;
      this._expressDays = parseInt(this.dataset.expressDays, 10) || 1;
      this.tick();
      this._timer = setInterval(this.tick.bind(this), 1000);
    }
    disconnectedCallback() {
      if (this._timer) clearInterval(this._timer);
    }
    tick() {
      var now = storeNow();
      var hour = now.getHours();
      var day = now.getDay();
      var workDay = isShipDay(day);
      var beforeCutoff = workDay && hour < this._cutoff;
      var locale = this.dataset.locale || 'fr-FR';
      var fallbacks = localizedFallbacks(locale);
      var tomorrowLabel = this.dataset.tomorrow || fallbacks.tomorrow;

      // Date d'expédition + livraison
      var shipDate = computeShipDate(now, this._cutoff);
      var expressDate = computeDeliveryDate(shipDate, this._expressDays);
      var classicDate = computeDeliveryDate(shipDate, this._days);

      // Affichage date livrée : un seul jour, le plus proche (express).
      var deliveryEl = this.querySelector('[data-bb-delivery-day]');
      if (deliveryEl) {
        var formatted = formatRelativeWeekday(expressDate, now, false, locale, tomorrowLabel);
        if (deliveryEl.textContent !== formatted) deliveryEl.textContent = formatted;
      }

      // Ligne expédition : "Expédié aujourd'hui" avant cutoff, sinon le prochain jour ouvré.
      var dispatchEl = this.querySelector('[data-bb-dispatch]');
      if (dispatchEl) {
        var dtxt = beforeCutoff
          ? (this.dataset.dispatchedToday || fallbacks.dispatchedToday)
          : (this.dataset.dispatchedPrefix || fallbacks.dispatchedPrefix) + ' ' + formatRelativeWeekday(shipDate, now, false, locale, tomorrowLabel);
        if (dispatchEl.textContent !== dtxt) dispatchEl.textContent = dtxt;
      }

      // Bandeau countdown : visible si avant cutoff jour ouvré + stock dispo
      var cdWrap = this.querySelector('[data-bb-countdown-wrap]');
      var cdTime = this.querySelector('[data-bb-countdown]');
      var stockClass = this.dataset.stockClass;
      var showCd = beforeCutoff && stockClass !== 'is-out';

      if (cdWrap) cdWrap.hidden = !showCd;

      if (showCd && cdTime) {
        var target = new Date(now);
        target.setHours(this._cutoff, 0, 0, 0);
        var diff = Math.max(0, Math.floor((target - now) / 1000));
        var h = Math.floor(diff / 3600);
        var m = Math.floor((diff % 3600) / 60);
        var txt = (this.dataset.countdownPrefix || fallbacks.countdownPrefix) + ' ' + h + ' h ' + pad(m) + ' min';
        if (cdTime.textContent !== txt) cdTime.textContent = txt;
      }

      // Description stock dynamique pour état "normal"
      var descEl = this.querySelector('.bb-sd__cell:first-child .bb-sd__d');
      if (descEl && stockClass === 'is-normal') {
        var newDesc = this.dataset.readyToShip || fallbacks.readyToShip;
        if (descEl.textContent !== newDesc) descEl.textContent = newDesc;
      }
    }
  }

  customElements.define('bb-stock-delivery', BbStockDelivery);
})();
